import { createAction } from '@reduxjs/toolkit';
import {
  takeLatest,
  select,
  put,
  debounce,
  takeEvery,
} from 'redux-saga/effects';
import { RootState } from 'data';
import {
  createProjectTemplateSubTask,
  createProjectTemplateTask,
} from 'data/templates/projects/slices';
import { rlNavigation } from 'utils/navigation';
import { TemplateId, TemplateState } from '../api';
import {
  allocationsBulkAction,
  createAutomationForSourceAction,
  saveProjectTemplate,
  toggleEnabledStateForAutomationByIdAction,
  updateAllAutomationAction,
  updateAllocatedMembersAction,
  updateAutomationByIdAction,
} from '../projects/actions';
import { templateSlice } from '../slices';

const { setDirtyState } = templateSlice.actions;

const checkProjectToTemplate =
  () =>
  ({ templates }: RootState): boolean =>
    templates.project.projectToTemplate;

const PROJECT_TEMPLATE_ACTION = '@@TEMPLATES/PROJECT/';
const DOCUMENT_TEMPLATE_ACTION = '@@TEMPLATES/DOCUMENT/';

function* watchAllTemplateSaga(): Generator {
  const currentPageId = rlNavigation.path()?.split('/')[3] as string;
  const isProjectToTemplate = yield select(checkProjectToTemplate());
  const dirtyObjectToSend: {
    id: TemplateId;
  } = {
    id:
      currentPageId === 'new' || isProjectToTemplate
        ? 'new'
        : parseInt(currentPageId, 10),
  };

  yield put(setDirtyState(dirtyObjectToSend));
}

function* watchAllDebouncedSavedChangesSaga(): Generator {
  yield put(
    saveProjectTemplate({
      type: TemplateState.Draft,
      saveToIdb: true,
    }),
  );
}

const debouncedSavedChanges = createAction(
  '@@TEMPLATES/PROJECTS/debouncedSavedChanges',
);

export function* watchDebouncedSaveProjectTemplateSaga(): Generator {
  yield debounce(
    0,
    debouncedSavedChanges.type,
    watchAllDebouncedSavedChangesSaga,
  );
}

function* watchAllTemplateSagaForIdb() {
  yield put(debouncedSavedChanges());
}

export function* watchAllTemplateChangesSaga(): Generator {
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplate`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}createProjectTemplate`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplateProjectPhase`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplateTask`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplateProjectPhases`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplateTasks`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}deletePhaseInProjectTemplate`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}shiftStartDayProjectPhaseInProjectTemplate`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}addProjectTemplateTaskDependency`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}addProjectTemplatePhaseDependency`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}updateTaskInProjectTemplate`,
    watchAllTemplateSaga,
  );
  yield takeLatest(createProjectTemplateTask.type, watchAllTemplateSaga);
  yield takeLatest(createProjectTemplateSubTask.type, watchAllTemplateSaga);
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}createPhaseInProjectTemplate`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplateParentOfTask`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}updateSpaceInProjectTemplate`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}deleteSpaceInProjectTemplate`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}setupSpaceTabsOnSpaces`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}deleteProjectTemplateSpaceTab`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${DOCUMENT_TEMPLATE_ACTION}createDocumentTemplateAction`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${DOCUMENT_TEMPLATE_ACTION}updateDocumentTemplateAction`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${DOCUMENT_TEMPLATE_ACTION}updateDocumentTemplateContentAction`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${DOCUMENT_TEMPLATE_ACTION}createDocumentTemplateTaskAction`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${DOCUMENT_TEMPLATE_ACTION}deleteDocumentTemplateTaskAction`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}JiraMapping`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}dynamicConditions`,
    watchAllTemplateSaga,
  );
  yield takeLatest(
    `${PROJECT_TEMPLATE_ACTION}deleteTaskInProjectTemplate`,
    watchAllTemplateSaga,
  );
  yield takeLatest(allocationsBulkAction, watchAllTemplateSaga);
  yield takeLatest(updateAllocatedMembersAction, watchAllTemplateSaga);
}

export function* watchAllTemplateChangesForIdbSaga(): Generator {
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}createProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplateProjectPhase`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplateTask`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplateProjectPhases`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplateTasks`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}deletePhaseInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}shiftStartDayProjectPhaseInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}addProjectTemplateTaskDependency`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}addProjectTemplatePhaseDependency`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}updateTaskInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(createProjectTemplateTask.type, watchAllTemplateSagaForIdb);
  yield takeEvery(
    createProjectTemplateSubTask.type,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}createPhaseInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}updateProjectTemplateParentOfTask`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}updateSpaceInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}deleteSpaceInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}setupSpaceTabsOnSpaces`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}deleteProjectTemplateSpaceTab`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}JiraMapping`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}dynamicConditions`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}deleteTaskInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(allocationsBulkAction, watchAllTemplateSagaForIdb);
  yield takeEvery(updateAllocatedMembersAction, watchAllTemplateSagaForIdb);
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}conditionalVariables`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}updatePhaseInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}addPhaseAboveInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}createTaskInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}createTaskBeforeInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}rearrangeTaskInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}addTaskBelowProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}addTaskAboveProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}addTaskDependencyInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}addPhaseDependencyInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}createSubTaskInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}createSpaceInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}setupSpaceInProjectTemplateAction`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}deleteSpaceTabInProjectTemplate`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}allocation/bulkAction`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    `${PROJECT_TEMPLATE_ACTION}allocation/updateMembers`,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(updateAllAutomationAction.type, watchAllTemplateSagaForIdb);
  yield takeEvery(updateAutomationByIdAction.type, watchAllTemplateSagaForIdb);
  yield takeEvery(
    createAutomationForSourceAction.type,
    watchAllTemplateSagaForIdb,
  );
  yield takeEvery(
    toggleEnabledStateForAutomationByIdAction.type,
    watchAllTemplateSagaForIdb,
  );
}
